// resoconto-api.js — amm-v3 (A3, ondata 30): parte PURA della vista
// "Resoconto d'insieme" (pagina 'resoconto' — matrice tipologia×mese con
// semantica null-vs-0 A3-080/A5-054, confronto anno-1, drill-down), testabile
// sotto node (contract test server/src/lib/amm-v3-resoconto.test.js).
//
// PRINCIPI (rito A1/A2/proc-v3):
//  - TESTI VERBATIM dal v1 (admin-module.js, renderResoconto r.2310-2434):
//    pinnati dal contract test.
//  - Le FORMULE della matrice replicate come funzioni pure — in particolare
//    la semantica null-vs-0 (r.2323-2335: null = nessuna fonte ha mai scritto
//    quel mese → '·' senza drill; 0 = zero REALE registrato → mostrato) e il
//    totale mese che PRESERVA null (r.2352-2360).
//  - STATO CONDIVISO col v1: anno su AMM.resocontoAnno (default anno corrente,
//    r.749), confronto su AMM.resocontoConfronto — flip di flag senza perdite.
//  - DELEGHE: openResocontoDrill (modale read-only con guardClose), fmtEur2,
//    toast. ZERO bypass di render (censiti: 3 handler interni) ⇒ NIENTE
//    interceptor.
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/**.

import { apiPathFn } from './api.js';

export const ARS_DELEGATED_METHODS = [
  'renderPage',         // il seam stesso (wrap) + refresh post-azione
  'renderResoconto',    // la vista v1 wrappata (drift guard)
  'fmtEur2',            // € a 2 decimali (unica fonte: v1, r.6855)
  'openResocontoDrill', // drill cella (modale read-only) — resta v1
  'toast',              // esito export CSV (stesso canale del v1)
];

export const ARS_API_PATHS = [
  'resoconto',       // GET /resoconto?anno=[&confronto=1]
  'export.download', // Esporta CSV (vista 'resoconto')
];

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test) ----
export const ARS_TEXTS = {
  title: "Resoconto d'insieme",
  sub: 'anno di cassa · previsto = scadenze, pagato = pagamenti effettivi',
  subConfrontoPre: ' · in grigio: ',
  confrontoOff: 'Confronta con anno precedente',
  confrontoOn: '✓ Confronta con anno precedente',
  exportCsv: 'Esporta CSV',
  kpiPrevisto: 'Previsto',
  kpiPagato: 'Pagato',
  kpiResiduo: 'Residuo',
  colTipologia: 'Tipologia',
  colTotale: 'Totale',
  rigaTotale: 'TOTALE',
  emptyPre: 'Nessun movimento nel ',
  emptyPost: '. Inserisci scadenze, F24 o cedolini per popolare il resoconto.',
  senzaMovimentiSuffix: ' tipologie senza movimenti',
  exportFallito: 'Export fallito: ',
};

// colori VERBATIM v1 (KPI r.2414-2416, dot fallback r.2322)
export const ARS_COLORS = { previsto: '#0f172a', pagato: '#059669', residuo: '#0e7490', dotFallback: '#cbd5e1' };

// ---- STATO condiviso col v1 ----

// anno e confronto vivono SU AMM (r.749 + r.2312; difese identiche al v1)
export function resocontoStato(A) {
  if (!A.resocontoAnno) A.resocontoAnno = new Date().getFullYear();
  return { anno: A.resocontoAnno, confronto: !!A.resocontoConfronto };
}

// ---- FORMULE v1 replicate (pure) — la semantica null-vs-0 ----

// stato della cella (r.2330-2331): vuota (·, senza drill) SOLO se ENTRAMBI
// null; lo 0 REALE non sparisce (A3-080)
export function cellaStato(mm) {
  const haPrevisto = mm.previsto != null;
  const haPagato = mm.pagato != null;
  return { vuota: !haPrevisto && !haPagato, haPrevisto, haPagato };
}

// totale del mese che PRESERVA null (r.2352-2360): nessun dato ⇒ null, non 0
export function totaleMese(tipologie, mese) {
  let previsto = null;
  let pagato = null;
  (tipologie || []).forEach((tp) => {
    const mm = tp.mesi[mese - 1] || {};
    if (mm.previsto != null) previsto = (previsto || 0) + mm.previsto;
    if (mm.pagato != null) pagato = (pagato || 0) + mm.pagato;
  });
  return { previsto, pagato };
}

// split con/senza movimenti (r.2318-2319)
export function splitMovimenti(tipologie) {
  const lista = tipologie || [];
  return {
    con: lista.filter((tp) => tp.tot_previsto || tp.tot_pagato),
    senza: lista.filter((tp) => !tp.tot_previsto && !tp.tot_pagato),
  };
}

// tutto a zero (r.2320) — il messaggio vuoto appare SOLO senza confronto
// (QUIRK r.2392, la decisione resta alla vista)
export function isTuttoZero(totali) {
  return !totali.previsto && !totali.pagato;
}

// aggancio tipologia anno-1 per id (r.2389)
export function trovaPrec(tipologiePrec, tp) {
  return (tipologiePrec || []).find((x) => x.id === tp.id);
}

// sottotitolo (r.2406), etichetta <details> (r.2399), stato vuoto (r.2393)
export function subTesto(confronto, anno) {
  return ARS_TEXTS.sub + (confronto ? ARS_TEXTS.subConfrontoPre + (anno - 1) : '');
}
export function senzaMovimentiLabel(n) {
  return n + ARS_TEXTS.senzaMovimentiSuffix;
}
export function emptyMessage(anno) {
  return ARS_TEXTS.emptyPre + anno + ARS_TEXTS.emptyPost;
}

// ---- LETTURA dati (default v1 r.2314-2317) ----
export async function readResoconto(win, anno, confronto) {
  const r = await apiPathFn(win, 'resoconto').call(null, anno, confronto);
  const rPrec = confronto ? (r && r.anno_precedente) : null;
  return {
    totali: (r && r.totali) || { previsto: 0, pagato: 0, residuo: 0 },
    tipologie: (r && r.tipologie) || [],
    tipologiePrec: rPrec ? (rPrec.tipologie || []) : null,
  };
}

// ---- AZIONI e DELEGHE (stessi punti del v1) ----
export function esportaCsvResoconto(win, anno) { return apiPathFn(win, 'export.download').call(null, 'resoconto', anno); }
export function apriDrill(win, anno, tp, mese) { win.AMM.openResocontoDrill(anno, tp, mese); }
